import _ from 'lodash';
import mixin from '../privates/utils/mixin';
import Element from '../ui/Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import PlayableMixin from './mixins/PlayableMixin';
import * as members from '../privates/privateMembers';
import imageUtils from '../privates/utils/imageUtils';
import isAllowedComponentAPI from '../privates/utils/componentAPISupport';
import ClickableMixin from './mixins/ClickableMixin';
import {validate} from '../privates/validations/validation';
import {GALLERY_CLICK_ACTIONS} from '../privates/constants';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';

function reportUnsupportedAPI(api, type) {
    reporter.reportSdkWarning(`'${api}' is not supported for an element of type: ${type}.`);
}

function getShowANavigationButtonsPropertyName(viewerType) {
    if (_.includes(['tpa.viewer.components.StripShowcase', 'tpa.viewer.components.Thumbnails'], viewerType)) {
        return 'arrowMode';
    }

    return 'showNavigation';
}

function executeApiIfSupported(comp, apiName, apiImplementation) {
    const viewerType = members._type.get(comp).viewerType;
    if (isAllowedComponentAPI(viewerType, apiName)) {
        return apiImplementation();
    }
    reportUnsupportedAPI(apiName, viewerType); //todo Shimi_Liderman 21/05/2017 18:53 Seems very strange to print out the viewer type!
}

/**
 * @typedef {external:Object} $w.Gallery~GalleryCapabilities
 * @summary An object used by the `galleryCapabilities` property that contains the capabilities of a gallery.
 * @property {external:Boolean} isPlayable Indicates if the gallery supports play operations.
 * @property {external:Boolean} hasCurrentItem Indicates if the gallery supports the notion of a current item.
 * @property {external:Boolean} hasNavigationButtons Indicates if the gallery supports navigation buttons.
 * @snippet [Gallery-galleryCapabilities_get.es6=Get the gallery's capabilities]
 * @see [galleryCapabilities](#galleryCapabilities)
 */

 /**
  * @typedef {external:Object} $w.Gallery~VideoItem
  * @summary An object used by the Gallery properties `items` and `currentItem` to represent a single gallery video.
  * @property {external:String} type Item type. Value is `"video"`.
  * @property {external:String} src Video source URL.
  * @property {external:String} slug Item slug.
  * @property {external:String} [title] Video title.
  * @property {external:String} [description] Video description. Descriptions over 100 characters are truncated.
  * @property {external:String} [link] URL of the video's clickable link. See [here]($w.LinkableMixin.html#link) for more information about links.
  * @property {external:String} [thumbnail] Video thumbnail URL.
  * @description
  *  A `VideoItem` can be used as an item in a Pro Gallery.
  *
  *  To determine if your gallery is a Pro Gallery or a standard gallery, hover
  *  over the gallery while it is not selected. The gallery type appears above
  *  the upper left corner of the gallery. If the type is "Wix Pro Gallery", then
  *  your gallery is a Pro Gallery. If the type is anything other than "Wix Pro
  *  Gallery", your gallery is a standard gallery.
  *
  *  The video source format is:
  *  `wix:video://v1/<video_uri>/<filename>#posterUri=<poster_uri>&posterWidth=<width>&posterHeight=<height>`
  * @snippet [Gallery-items_set.es6=Set the list of items for a gallery]
  * @see [items](#items)
  * @see [currentItem](#currentItem)
  */

 /**
  * @typedef {external:Object} $w.Gallery~ImageItem
  * @summary An object used by the Gallery properties `items` and `currentItem` to represent a single gallery image.
  * @property {external:String} type Item type. Value is `"image"`.
  * @property {external:String} src Image source URL.
  * @property {external:String} slug Item slug.
  * @property {external:String} [title] Image title.
  * @property {external:String} [description] Image description. Descriptions over 100 characters are truncated.
  * @property {external:String} [link] URL of the image's clickable link. See [here]($w.LinkableMixin.html#link) for more information about links.
  * @description
  *  The `src` property of a `ImageItem` can be:
  *
  *  + An image from the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>.
  *  + An external image from any web location (some galleries do not support external images).
  *
  *  The image source format for Media Manager images is:
  *  `wix:image://v1/<uri>/<filename>#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
  * @snippet [Gallery-items_set.es6=Set the list of items for a gallery]
  * @see [items](#items)
  * @see [currentItem](#currentItem)
  */

/**
 * @class Gallery
 * @summary A gallery for displaying multiple items.
 * @classdesc
 *  There are many different types and styles of galleries, some of which do not
 *  support all of the properties and functions described below. To determine
 *  what functionality a specific gallery supports, use its
 *  [`galleryCapabilities`](#galleryCapabilities) property to identify its
 *  capabilities. Then use the list below to determine what functionality is
 *  supported by the gallery.
 *
 *  + All galleries
 *    + [`clickAction`](#clickAction)
 *    + [`collapsed`](#collapsed)
 *    + [`galleryCapabilities`](#galleryCapabilities)
 *    + [`global`](#global)
 *    + [`hidden`](#hidden)
 *    + [`id`](#id)
 *    + [`items`](#items)
 *    + [`isVisible`](#isVisible)
 *    + [`parent`](#parent)
 *    + [`rendered`](#rendered)
 *    + [`type`](#type)
 *    + [`collapse()`](#collapse)
 *    + [`expand()`](#expand)
 *    + [`hide()`](#hide)
 *    + [`onClick()`](#onClick)
 *    + [`onDblClick()`](#onDblClick)
 *    + [`onItemClicked()`](#onItemClicked)
 *    + [`onMouseIn()`](#onMouseIn)
 *    + [`onMouseOut()`](#onMouseOut)
 *    + [`onViewportEnter()`](#onViewportEnter)
 *    + [`onViewportLeave()`](#onViewportLeave)
 *    + [`scrollTo()`](#scrollTo)
 *    + [`show()`](#show)
 *  + Galleries where `hasCurrentItem` is `true`
 *    + [`currentIndex`](#currentIndex)
 *    + [`currentItem`](#currentItem)
 *    + [`onCurrentItemChanged()`](#onCurrentItemChanged)
 *  + Galleries where `isPlayable` is `true`
 *    + [`isPlaying`](#isPlaying)
 *    + [`next()`](#next)
 *    + [`onPause()`](#onPause)
 *    + [`onPlay()`](#onPlay)
 *    + [`pause()`](#pause)
 *    + [`play()`](#play)
 *    + [`previous()`](#previous)
 *  + Galleries where `hasNavigationButtons` is `true`
 *    + [`showNavigationButtons`](#showNavigationButtons)
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.PlayableMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.SlideShowGallery
 * @viewername wysiwyg.viewer.components.SliderGallery
 * @viewername wysiwyg.viewer.components.MatrixGallery
 * @viewername wysiwyg.viewer.components.PaginatedGridGallery
 * @viewername tpa.viewer.components.Thumbnails
 * @viewername tpa.viewer.components.StripShowcase
 * @viewername tpa.viewer.components.StripSlideshow
 * @viewername tpa.viewer.components.Collage
 * @viewername tpa.viewer.components.Masonry
 * @viewername tpa.viewer.components.Honeycomb
 * @viewername tpa.viewer.components.Accordion
 * @viewername wysiwyg.viewer.components.tpapps.TPA3DCarousel
 * @viewername wysiwyg.viewer.components.tpapps.TPA3DGallery
 * @viewername tpa.viewer.components.Impress
 * @viewername tpa.viewer.components.Freestyle
 * @definitionId wysiwyg.viewer.components.SlideShowGallery
 * @definitionId wysiwyg.viewer.components.SliderGallery
 * @definitionId wysiwyg.viewer.components.MatrixGallery
 * @definitionId wysiwyg.viewer.components.PaginatedGridGallery
 * @definitionId tpa.viewer.components.Thumbnails
 * @definitionId tpa.viewer.components.StripShowcase
 * @definitionId tpa.viewer.components.StripSlideshow
 * @definitionId tpa.viewer.components.Collage
 * @definitionId tpa.viewer.components.Masonry
 * @definitionId tpa.viewer.components.Honeycomb
 * @definitionId tpa.viewer.components.Accordion
 * @definitionId wysiwyg.viewer.components.tpapps.TPA3DCarousel
 * @definitionId wysiwyg.viewer.components.tpapps.TPA3DGallery
 * @definitionId tpa.viewer.components.Impress
 * @definitionId tpa.viewer.components.Freestyle
 * @tagname gallery
 */
class Gallery extends Element {

    /**
     * @member clickAction
     * @label property
     * @syntax
     * get clickAction(): string
     * set clickAction(value: ClickActionType): void
     * type ClickActionType = "expand" | "link" | "none"
     * @summary Sets or gets the action that occurs when an item in the gallery is clicked.
     * @description
     *  Setting the `clickAction` property sets what happens when
     *  an item in the gallery is clicked.
     *
     *  The value can be set to:
     *
     *  + `"none"`: Nothing happens.
     *  + `"expand"`: The item opens in a popup window.
     *  + `"link"`: The item's link opens.
     *
     *
     *  Getting the `clickAction` property returns what happens when
     *  an item in the gallery is clicked.
     * 
     * @note You can only get the click action of a Pro Gallery using the `clickAction` property
     *  if you set the click action using the `clickAction` property. If you set the
     *  click action in the Editor, you are not be able to retrieve it with the `clickAction` property.
     *  This limitation does not apply to standard galleries.
     *
     *  To determine if your gallery is a Pro Gallery, hover over the gallery while
     *  it is not selected. The gallery type appears above the upper left corner
     *  of the gallery. If the type is "Wix Pro Gallery", then your gallery is a
     *  Pro Gallery. If the type is anything other than "Wix Pro Gallery", the
     *  limitation described above does not apply to your gallery.
     * @snippet [Gallery-clickAction_get.es6=Get the action that occurs when an item in the gallery is clicked]
     * @snippet [Gallery-clickAction_set.es6=Set the action that occurs when an item in the gallery is clicked]
     * @type {external:string}
     * @memberof $w.Gallery
     * @instance
     */
    get clickAction() {
        return executeApiIfSupported(this, 'clickAction', () => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const galleryImageClickAction = RMI.getProps(compId).galleryImageOnClickAction;
            return _.findKey(GALLERY_CLICK_ACTIONS, (v) => v === galleryImageClickAction);
        });
    }

    /**
     * @member clickAction
     * @memberof $w.Gallery
     * @param {external:string} action The action that occurs when an item in the gallery is clicked.
     */
    set clickAction(action) {
        return executeApiIfSupported(this, 'clickAction', () => {
            const compType = members._type.get(this).compType;
            if (validate({
                compType,
                functionName: 'clickAction',
                dataToValidate: {galleryImageOnClickAction: action},
                compName: this.id
            })) {
                const galleryImageClickAction = _.isNil(action) ? GALLERY_CLICK_ACTIONS.none : GALLERY_CLICK_ACTIONS[action];
                const RMI = members._RMI.get(this);
                const compId = members._compId.get(this);
                RMI.setProps(compId, {galleryImageOnClickAction: galleryImageClickAction});
            }
        });
    }

    /**
     * @member currentIndex
     * @label property
     * @syntax
     * get currentIndex(): Number
     * @summary Gets the index of the gallery's current item.
     * @description
     *  The indices of the items in a gallery are zero-based. For example, if
     *  the third item is currently being displayed, then `currentIndex`
     *  returns `2`.
     *
     * @note
     *  The `currentIndex` property is supported by galleries where the
     *  `hasCurrentItem` [capability](#galleryCapabilities) is `true`.
     *
     *  If you [change the gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `currentIndex` and you try to use it, your code may no longer function correctly.
     * @snippet [Gallery-currentIndex_get.es6=Get the index of the gallery's current item]
     * @type {external:Number}
     * @memberof $w.Gallery
     * @instance
     * @readonly
     */
    get currentIndex() {
        return executeApiIfSupported(this, 'currentIndex', () => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const compState = RMI.getState(compId);
            return compState.currentIndex;
        });
    }

    /**
     * @member items
     * @label property
     * @syntax
     * get items(): (ImageItem | VideoItem)[]
     * set items(value: (ImageItem | VideoItem)[]): void
     * @summary Sets or gets the items in a gallery.
     * @description
     *  Setting the `items` property sets the gallery items that make up the gallery.
     *
     *  Set `items` to an empty array (`[]`) to remove the current gallery items.
     *
     *  Getting the `items` property returns the current list of gallery items
     *  that make up the gallery.
     *
     *  You cannot modify the gallery item array in-place. To add, change, or
     *  remove individual gallery items:
     *
     *    1. Store the value of the `items` property in a variable.
     *    1. Make changes to the gallery items array.
     *    1. Reset the `items` property with the modified array.
     *
     *
     * @note You can only get the items of a Pro Gallery using the `items` property
     *  if you set its items using the `items` property or connect the gallery
     *  to a dataset first. If you set the gallery's items in the Editor, you
     *  are not be able to retrieve them with the `items` property. This limitation
     *  does not apply to standard galleries.
     *
     *  To determine if your gallery is a Pro Gallery, hover over the gallery while
     *  it is not selected. The gallery type appears above the upper left corner
     *  of the gallery. If the type is "Wix Pro Gallery", then your gallery is a
     *  Pro Gallery. If the type is anything other than "Wix Pro Gallery", the
     *  limitation described above does not apply to your gallery.
     * @snippet [Gallery-items_get.es6=Get the list of items and the first item's information]
     * @snippet [Gallery-items_set.es6=Set the list of items for a gallery]
     * @snippet [Gallery-add.es6=Add an item to a gallery]
     *  This example retrieves the items of a gallery, adds a new
     *  item, and then overwrites the old items.
     * @type {($w.Gallery~ImageItem[]|$w.Gallery~VideoItem[])}
     * @memberof $w.Gallery
     * @instance
     */
    get items() {
        return executeApiIfSupported(this, 'items', () => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const data = RMI.getData(compId);
            const imageItems = imageUtils.filterImageItems(data.items);
            return _.map(data.items, (item, index) => {
                if (_.includes(imageItems, item)) {
                    return imageUtils.dataFormat.toUser(item, index);
                }
                return item;
            });
        });
    }

  /**
   * @member items
   * @memberof $w.Gallery
   * @param {($w.Gallery~ImageItem[]|$w.Gallery~VideoItem[])} items
   */
    set items(items) {
        return executeApiIfSupported(this, 'items', () => {
            const compType = members._type.get(this).compType;
            if (!validate({
                compType,
                functionName: 'items',
                dataToValidate: {items},
                compName: this.id
            })) {
                return this;
            }
            const imageItems = imageUtils.filterImageItems(items);
            if (_.size(imageItems) !== _.size(items)) {
                reporter.reportSdkWarning(generateError.warning_non_images_in_gallery({
                    galleryId: this.id
                }));
            }
            const isImagesDataValid = _.every(imageItems,
                (imageData, imageIndex) => imageUtils.checkImageValidation({imageData, imageIndex, compName: this.id}));

            if (!isImagesDataValid) {
                return;
            }

            const newImageItems = _.map(imageItems, imageUtils.dataFormat.toModel);

            if(!_.every(newImageItems)){
                return;
            }

            const RMI = members._RMI.get(this);
            RMI.setData(members._compId.get(this), {items: newImageItems});
        });
    }

    /**
     * @function onCurrentItemChanged
     * @syntax
     * function onCurrentItemChanged(handler: GalleryChangeEventHandler): Gallery
     * callback GalleryChangeEventHandler(event: GalleryItemChangedEvent, $w: Function): void
     * @summary Adds an event handler that runs when a gallery's current item changes.
     * @description
     *  A gallery's current item changes through playing or certain user
     *  interactions.
     *
     * @note
     *  The `onCurrentItemChanged()` function is supported by galleries where
     *  the `hasCurrentItem` [capability](#galleryCapabilities) is `true`.
     *
     *  If you [change the gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `hasCurrentItem` and you try to use it, your
     *  code may no longer function correctly.
     *
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.GalleryItemChangedEventHandler} handler The name of the
     *  function or the function expression to run when the gallery's current
     *  item changes.
     * @returns {$w.Gallery} The gallery that triggered the event.
     * @snippet [Gallery-onCurrentItemChanged.es6=Get the information of the item that changed]
     * @memberof $w.Gallery
     * @eventtype imageChanged
     * @eventhandler
     */
    onCurrentItemChanged(cb) {
        executeApiIfSupported(this, 'onCurrentItemChanged', () => {
            const compType = members._type.get(this).compType;
            if (validate({
                compType,
                functionName: 'onCurrentItemChanged',
                dataToValidate: {onChange: cb},
                compName: this.id
            })) {
                const RMI = members._RMI.get(this);
                const contextId = members._contextId.get(this);
                const controllerId = members._controllerId.get(this);
                const compId = members._compId.get(this);
                RMI.registerEvent(contextId, compId, RMI.getEventTypes().IMAGE_CHANGED, controllerId, cb);
            }
        });
        return this;
    }

    /**
     * @function onItemClicked
     * @syntax
     * function onItemClicked(handler: GalleryItemClickedEventHandler): Gallery
     * callback GalleryItemClickedEventHandler(event: GalleryItemClickedEvent, $w: Function): void
     * @summary Adds an event handler that runs when an item in a gallery is
     *  clicked.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.GalleryItemClickedEventHandler} handler The name of the
     *  function or the function expression to run when a gallery item is
     *  clicked.
     * @returns {$w.Gallery} The gallery that triggered the event.
     * @snippet [Gallery-onItemClicked.es6=Get the information of the item that was clicked]
     * @see [clickAction](#clickAction)
     * @memberof $w.Gallery
     * @eventtype itemClicked
     * @eventhandler
     */
    onItemClicked(cb) {
        executeApiIfSupported(this, 'onItemClicked', () => {
            const compType = members._type.get(this).compType;
            if (validate({
                compType,
                functionName: 'onItemClicked',
                dataToValidate: {onItemClicked: cb},
                compName: this.id
            })) {
                const RMI = members._RMI.get(this);
                const contextId = members._contextId.get(this);
                const controllerId = members._controllerId.get(this);
                const compId = members._compId.get(this);
                RMI.registerEvent(contextId, compId, RMI.getEventTypes().ITEM_CLICKED, controllerId, cb);
            }
        });
        return this;
    }

    /**
     * @member showNavigationButtons
     * @label property
     * @syntax
     * get showNavigationButtons(): boolean
     * set showNavigationButtons(value: boolean): void
     * @summary Determines if a gallery's navigation arrows are shown.
     * @description
     *  A gallery's navigation arrows allow a user to click through the items in
     *  the gallery.
     * @note
     *  The `showNavigationButtons` property is supported by galleries where
     *  the `hasNavigationButtons` [capability](#galleryCapabilities) is `true`.
     *
     *  If you [change the gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `hasNavigationButtons` and you try to use
     *  it, your code may no longer function correctly.
     * @snippet [Gallery-showNavigationButtons_get.es6=Get whether navigation buttons are shown]
     * @snippet [Gallery-showNavigationButtons_set.es6=Set navigation buttons to be hidden]
     * @snippet [Gallery-showNavigationButtons_toggle.es6=Toggle whether navigation buttons are shown]
     * @type {external:Boolean}
     * @memberof $w.Gallery
     * @instance
     */
    get showNavigationButtons() {
        return executeApiIfSupported(this, 'showNavigationButtons', () => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const props = RMI.getProps(compId);
            const viewerType = members._type.get(this).viewerType;
            const navButtonsPropName = getShowANavigationButtonsPropertyName(viewerType);
            return props[navButtonsPropName];
        });
    }

    /**
     * @member showNavigationButtons
     * @memberof $w.Gallery
     * @param {external:Boolean} value
     */
    set showNavigationButtons(value) {
        return executeApiIfSupported(this, 'showNavigationButtons', () => {
            const compType = members._type.get(this).compType;
            const viewerType = members._type.get(this).viewerType;
            const navButtonsPropName = getShowANavigationButtonsPropertyName(viewerType);
            if (validate({
                compType,
                functionName: 'showNavigationButtons',
                dataToValidate: {[navButtonsPropName]: value},
                compName: this.id
            })) {
                const RMI = members._RMI.get(this);
                const compId = members._compId.get(this);
                RMI.setProps(compId, {[navButtonsPropName]: value});
            }
        });
    }

    /**
     * @member currentItem
     * @label property
     * @syntax
     * get currentItem(): ImageItem | VideoItem
     * @summary Gets an object containing information about the current item.
     * @snippet [Gallery-currentItem_get.es6=Get the current gallery item]
     * @note
     *  The `currentItem` property is supported by galleries where the
     *  `hasCurrentItem` [capability](#galleryCapabilities) is `true`.
     *
     *  If you [change the gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `hasCurrentItem` and you try to use
     *  it, your code may no longer function correctly.
     * @type {$w.Gallery~ImageItem|$w.Gallery~VideoItem}
     * @memberof $w.Gallery
     * @instance
     * @readonly
     */
    get currentItem() {
        return executeApiIfSupported(this, 'currentItem', () => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const compState = RMI.getState(compId);
            const currentIndex = compState.currentIndex;
            const items = _.get(RMI.getData(compId), 'items');
            if (_.isUndefined(items) || _.isUndefined(currentIndex) || _.size(items) === 0) {
                return undefined;
            }
            const image = imageUtils.dataFormat.toUser(items[currentIndex]);
            return _.omit(image, 'id');
        });
    }

    /**
     * @member galleryCapabilities
     * @label property
     * @syntax
     * get galleryCapabilities(): GalleryCapabilities
     * @summary Gets an object containing information about the gallery's capabilities.
     * @snippet [Gallery-galleryCapabilities_get.es6=Get the gallery's capabilities]
     * @type {$w.Gallery~GalleryCapabilities}
     * @memberof $w.Gallery
     * @instance
     * @readonly
     */
    get galleryCapabilities() {
        const viewerType = members._type.get(this).viewerType;
        return Object.freeze({
            isPlayable: isAllowedComponentAPI(viewerType, 'play'),
            hasCurrentItem: isAllowedComponentAPI(viewerType, 'currentItem'),
            hasNavigationButtons: isAllowedComponentAPI(viewerType, 'showNavigationButtons'),
            supportsAllMediaTypes: false,
            isAnimatable: this.isAnimatable
        });
    }

    toJSON() {
        const viewerType = members._type.get(this).viewerType;
        const possibleProperties = {
            clickAction: isAllowedComponentAPI(viewerType, 'clickAction') ? this.clickAction : null,
            currentIndex: isAllowedComponentAPI(viewerType, 'currentIndex') ? this.currentIndex : null,
            currentItem: isAllowedComponentAPI(viewerType, 'currentItem') ? this.currentItem : null,
            showNavigationButtons: isAllowedComponentAPI(viewerType, 'showNavigationButtons') ? this.showNavigationButtons : null,
            items: isAllowedComponentAPI(viewerType, 'items') ? this.items : null
        };
        const {id, type, global, rendered} = this;
        const result = _.assign({
            id,
            type,
            global,
            rendered
        }, possibleProperties);

        return _.omitBy(result, _.isNull);
    }
}

mixin(Gallery.prototype, HiddenCollapsedMixin.prototype);
mixin(Gallery.prototype, PlayableMixin.prototype);
mixin(Gallery.prototype, ClickableMixin.prototype);

export default Gallery;
